import React, { useState, useRef, useEffect } from 'react';
import { getEndpoint } from '../config/api';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Animated,
  StatusBar,
  Dimensions,
  Platform,
  SafeAreaView,
  ActivityIndicator,
} from 'react-native';
import { COLORS } from '../constants/theme';

const { width, height } = Dimensions.get('window');

interface ResetPasswordScreenProps {
  token: string;
  onBackPress: () => void;
  onSuccess: () => void;
}

export const ResetPasswordScreen: React.FC<ResetPasswordScreenProps> = ({ 
  token,
  onBackPress, 
  onSuccess 
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const slideInAnimation = useRef(new Animated.Value(width)).current;
  const fadeInAnimation = useRef(new Animated.Value(0)).current;
  const shakeAnimation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.parallel([
      Animated.timing(slideInAnimation, {
        toValue: 0,
        duration: 800,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(fadeInAnimation, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]);

    animation.start();
  }, []);

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      setErrorMessage('Please enter a new password');
      triggerShakeAnimation();
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage('Password must be at least 6 characters long');
      triggerShakeAnimation();
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      triggerShakeAnimation();
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const response = await fetch(getEndpoint('/api/auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          newPassword: newPassword,
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccessMessage('Password updated successfully');
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setErrorMessage(data.message || 'Failed to reset password');
        triggerShakeAnimation();
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      setErrorMessage('Failed to reset password. Please try again.');
      triggerShakeAnimation();
    } finally {
      setIsLoading(false);
    }
  };

  const triggerShakeAnimation = () => {
    Animated.sequence([
      Animated.timing(shakeAnimation, {
        toValue: 10,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(shakeAnimation, {
        toValue: -10,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(shakeAnimation, {
        toValue: 10,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(shakeAnimation, {
        toValue: 0,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar 
        barStyle="dark-content" 
        backgroundColor="#FFFFFF"
        translucent={Platform.OS === 'android'}
      />
      
      <Animated.View
        style={[
          styles.content,
          {
            transform: [
              { translateX: slideInAnimation },
              { translateX: shakeAnimation },
            ],
            opacity: fadeInAnimation,
          },
        ]}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={onBackPress}
            activeOpacity={0.7}
            disabled={isLoading}
          >
            <Text style={styles.backButtonText}>‹</Text>
          </TouchableOpacity>
        </View>

        {/* Title */}
        <Text style={styles.title}>Reset Password</Text>
        <Text style={styles.subtitle}>
          Enter your new password below.
        </Text>

        {/* Error Message */}
        {errorMessage ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{errorMessage}</Text>
          </View>
        ) : null}

        {/* Success Message */}
        {successMessage ? (
          <View style={styles.successContainer}>
            <Text style={styles.successText}>{successMessage}</Text>
          </View>
        ) : null}

        {/* Form */}
        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="New password"
              placeholderTextColor="#9CA3AF"
              value={newPassword}
              onChangeText={(text) => {
                setNewPassword(text);
                setErrorMessage('');
                setSuccessMessage('');
              }}
              secureTextEntry={!showPassword}
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isLoading}
            />
            <TouchableOpacity
              style={styles.eyeButton}
              onPress={togglePasswordVisibility}
              activeOpacity={0.7}
              disabled={isLoading}
            >
              <Text style={styles.eyeIcon}>👁</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Confirm new password"
              placeholderTextColor="#9CA3AF"
              value={confirmPassword}
              onChangeText={(text) => {
                setConfirmPassword(text);
                setErrorMessage('');
                setSuccessMessage('');
              }}
              secureTextEntry={!showConfirmPassword}
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isLoading}
            />
            <TouchableOpacity
              style={styles.eyeButton}
              onPress={toggleConfirmPasswordVisibility}
              activeOpacity={0.7}
              disabled={isLoading}
            >
              <Text style={styles.eyeIcon}>👁</Text>
            </TouchableOpacity>
          </View>

          {/* Reset Button */}
          <TouchableOpacity
            style={[
              styles.resetButton,
              isLoading && styles.resetButtonDisabled
            ]}
            onPress={handleResetPassword}
            activeOpacity={0.8}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Text style={styles.resetButtonText}>Reset Password</Text>
            )}
          </TouchableOpacity>

          {/* Back to Login */}
          <TouchableOpacity
            style={styles.backToLoginButton}
            onPress={onBackPress}
            activeOpacity={0.7}
            disabled={isLoading}
          >
            <Text style={styles.backToLoginText}>Back to Login</Text>
          </TouchableOpacity>
        </View>
      </Animated.View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    flex: 1,
    paddingHorizontal: Platform.select({ ios: 24, android: 24, default: 20 }),
    paddingTop: Platform.select({ ios: 130, android: 130, default: 130 }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Platform.select({ ios: 40, android: 40, default: 32 }),
  },
  backButton: {
    width: Platform.select({ ios: 40, android: 40, default: 36 }),
    height: Platform.select({ ios: 40, android: 40, default: 36 }),
    borderRadius: Platform.select({ ios: 20, android: 20, default: 18 }),
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  backButtonText: {
    fontSize: Platform.select({ ios: 24, android: 24, default: 20 }),
    color: '#374151',
    fontWeight: '300',
  },
  title: {
    fontSize: Platform.select({ ios: 32, android: 32, default: 28 }),
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: Platform.select({ ios: 16, android: 16, default: 12 }),
    textAlign: 'center',
    fontFamily: 'Montserrat_700Bold',
  },
  subtitle: {
    fontSize: Platform.select({ ios: 16, android: 16, default: 14 }),
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: Platform.select({ ios: 24, android: 24, default: 20 }),
    marginBottom: Platform.select({ ios: 40, android: 40, default: 32 }),
    fontFamily: 'Montserrat_400Regular',
  },
  errorContainer: {
    backgroundColor: '#FEF2F2',
    borderWidth: 1,
    borderColor: '#FECACA',
    borderRadius: Platform.select({ ios: 8, android: 8, default: 6 }),
    padding: Platform.select({ ios: 12, android: 12, default: 10 }),
    marginBottom: Platform.select({ ios: 20, android: 20, default: 16 }),
  },
  errorText: {
    color: '#DC2626',
    fontSize: Platform.select({ ios: 14, android: 14, default: 12 }),
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
  },
  successContainer: {
    backgroundColor: '#F0FDF4',
    borderWidth: 1,
    borderColor: '#BBF7D0',
    borderRadius: Platform.select({ ios: 8, android: 8, default: 6 }),
    padding: Platform.select({ ios: 12, android: 12, default: 10 }),
    marginBottom: Platform.select({ ios: 20, android: 20, default: 16 }),
  },
  successText: {
    color: '#16A34A',
    fontSize: Platform.select({ ios: 14, android: 14, default: 12 }),
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
  },
  form: {
    flex: 1,
  },
  inputContainer: {
    position: 'relative',
    marginBottom: Platform.select({ ios: 20, android: 20, default: 16 }),
  },
  input: {
    height: Platform.select({ ios: 56, android: 56, default: 48 }),
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: Platform.select({ ios: 12, android: 12, default: 10 }),
    paddingHorizontal: Platform.select({ ios: 16, android: 16, default: 14 }),
    fontSize: Platform.select({ ios: 16, android: 16, default: 14 }),
    color: '#111827',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Montserrat_400Regular',
  },
  eyeButton: {
    position: 'absolute',
    right: Platform.select({ ios: 16, android: 16, default: 14 }),
    top: Platform.select({ ios: 16, android: 16, default: 14 }),
    width: Platform.select({ ios: 24, android: 24, default: 20 }),
    height: Platform.select({ ios: 24, android: 24, default: 20 }),
    justifyContent: 'center',
    alignItems: 'center',
  },
  eyeIcon: {
    fontSize: Platform.select({ ios: 16, android: 16, default: 14 }),
  },
  resetButton: {
    backgroundColor: COLORS.primary,
    height: Platform.select({ ios: 56, android: 56, default: 48 }),
    borderRadius: Platform.select({ ios: 12, android: 12, default: 10 }),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: Platform.select({ ios: 24, android: 24, default: 20 }),
  },
  resetButtonDisabled: {
    backgroundColor: '#9CA3AF',
  },
  resetButtonText: {
    color: '#FFFFFF',
    fontSize: Platform.select({ ios: 18, android: 18, default: 16 }),
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  backToLoginButton: {
    alignItems: 'center',
    paddingVertical: Platform.select({ ios: 16, android: 16, default: 12 }),
  },
  backToLoginText: {
    color: COLORS.primary,
    fontSize: Platform.select({ ios: 16, android: 16, default: 14 }),
    fontWeight: '500',
    fontFamily: 'Montserrat_500Medium',
  },
});
